// The stages a mosquito passes through between egg and adult, and the
// per-species arithmetic the Home weather card draws its timeline with.
//
// One number per species is real: the egg → adult total the payload carries as
// `HomeEggToAdultSpecies.degree_days` — Ae. aegypti 105.5 DD °C, Cx.
// quinquefasciatus 137.0, Cx. tarsalis 145.4, base 10 °C. How that total splits
// between egg, larva and pupa is not in the payload, no per-instar (L1–L4)
// development data exists anywhere in the tree, and the per-stage
// `species_params` JSON the data package names was not in the upload. The six
// developmental stages — egg, the four instars and pupa — are therefore given
// an equal share of it below: a shape deliberately too even to be mistaken for
// measurement, so the graphic shows where a stage sits in the sequence without
// claiming to know how long it lasts. Adult is the end point of that sequence,
// not a seventh period, so it closes the line at the total.
import type { HomeEggToAdult } from "@/type/api";

/** The stages in the order development passes through them, ending at the adult
 *  the sequence arrives at, each with the single letter the timeline draws
 *  inside its dot: E, 1, 2, 3, 4, P, A. */
export const DEVELOPMENT_STAGES = [
	{ stage: "Egg", letter: "E" },
	{ stage: "1st instar", letter: "1" },
	{ stage: "2nd instar", letter: "2" },
	{ stage: "3rd instar", letter: "3" },
	{ stage: "4th instar", letter: "4" },
	{ stage: "Pupa", letter: "P" },
	{ stage: "Adult", letter: "A" },
] as const;

export type DevelopmentStage = (typeof DEVELOPMENT_STAGES)[number]["stage"];

/**
 * The share of the egg → adult total each developmental stage is provisionally
 * given. The divisor is six, not seven: egg, the four instars and pupa are the
 * periods the total is spent in, and adult is the end point of the sequence
 * rather than another period — the last mark sits at the total itself. Not
 * data: the only real value is the total, and the per-stage degree days the
 * split would need are not in the payload (see the module comment). An even
 * sixth each is a placeholder the caption under the rows admits to.
 */
export const PROVISIONAL_STAGE_SHARE = 1 / (DEVELOPMENT_STAGES.length - 1);

/** One stage's place on a species' line, as the timeline draws it. */
export interface DevelopmentStageMark {
	stage: DevelopmentStage;
	/** The letter drawn inside this stage's dot: E, 1, 2, 3, 4, P or A. */
	letter: string;
	/**
	 * Days from egg to the point this dot sits on: the stage's day offset on
	 * the shared scale. Each dot marks the start of its stage, so egg is 0 and
	 * adult — the end point of the sequence — sits at the species' total.
	 */
	offsetDays: number;
	/**
	 * Days this stage takes at the payload's current rate, and 0 for adult,
	 * which is arrived at rather than passed through.
	 */
	days: number;
	/**
	 * Days from the start of this stage to adult: this stage's own duration
	 * plus every developmental stage after it, which is what is left of the
	 * species' line past the dot. Egg therefore reads the whole egg → adult
	 * span, pupa one share, and adult — already there — nothing.
	 */
	daysToAdult: number;
}

/** One species' egg → adult line: its own dates, drawn from egg to its total. */
export interface SpeciesStageTrack {
	species: string;
	/** Egg → adult days for this species — where its line ends. */
	days: number;
	/** The payload's egg → adult requirement, in DD °C. */
	degreeDays: number;
	/** The stages, in order, each with its offset and days to adult. */
	marks: DevelopmentStageMark[];
}

/** Every species' line, and the shared day scale they are all drawn on. */
export interface SpeciesStageTracks {
	tracks: SpeciesStageTrack[];
	/** The shared axis maximum in days: the slowest species' egg → adult span. */
	axisMaxDays: number;
}

/**
 * Each species' egg → adult line, plus the axis maximum every line shares.
 * Rows run from 0 (egg laid) to the species' own total, so the quicker species
 * visibly fall short of the slowest one.
 */
export function buildSpeciesStageTracks(
	eggToAdult: HomeEggToAdult,
): SpeciesStageTracks {
	const tracks: SpeciesStageTrack[] = eggToAdult.species.map((species) => {
		// An equal share each for now, because the split the real per-stage
		// degree days would need is not in the payload (see the module comment).
		// Six shares over the species' total: egg through pupa.
		const stageDays = species.days * PROVISIONAL_STAGE_SHARE;

		const marks: DevelopmentStageMark[] = DEVELOPMENT_STAGES.map(
			({ stage, letter }, index) => {
				// Adult closes the sequence, so its dot marks the total the six
				// developmental stages add up to rather than another start.
				const adult = index === DEVELOPMENT_STAGES.length - 1;
				const offsetDays = adult ? species.days : index * stageDays;
				return {
					stage,
					letter,
					offsetDays,
					days: adult ? 0 : stageDays,
					// What is left of the line beyond the dot: the stage's own
					// duration and everything after it. Measured against the
					// species' total, so the egg mark is the total exactly.
					daysToAdult: species.days - offsetDays,
				};
			},
		);

		return {
			species: species.species,
			days: species.days,
			degreeDays: species.degree_days,
			marks,
		};
	});

	// The longest line sets the scale; a payload with no species has nothing to
	// scale, so the maximum stays 0 and the caller draws no scale at all.
	const axisMaxDays = tracks.reduce(
		(max, track) => Math.max(max, track.days),
		0,
	);
	return { tracks, axisMaxDays };
}
